/* BrandOS: payment preference is confirmed in the cart, never before add-to-cart. */
(() => {
  'use strict';
  if (window.__brandosCartPaymentV5) return;
  window.__brandosCartPaymentV5 = true;
  const rootPath = () => window.Shopify?.routes?.root || '/';
  const boxes = () => [...document.querySelectorAll('[data-brandos-payment-cart]')];
  const validMode = value => value === 'prepaid' || value === 'cod';
  const modeFromCart = cart => {
    const value = cart.attributes?.brandos_payment_preference;
    return value === 'PREPAID' ? 'prepaid' : value === 'COD' ? 'cod' : '';
  };
  const codesFromCart = cart => (cart.discount_codes || []).map(entry => entry.code).filter(Boolean);
  let desiredMode = '';
  let runner = null;
  let checkoutBusy = false;
  const allowedSubmits = new WeakSet();

  const prompt = (message, error = false) => boxes().forEach(box => {
    const node = box.querySelector('[data-brandos-choice-error]');
    if (!node) return;
    node.textContent = message;
    node.dataset.error = String(error);
  });
  const paint = mode => boxes().forEach(box => {
    box.dataset.brandosPaymentMode = mode;
    box.querySelectorAll('[data-brandos-cart-choice]').forEach(input => { input.checked = input.value === mode; });
  });
  const money = (amount, currency = 'INR') => new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(amount / 100);
  const renderTotals = cart => {
    // A discount can change line-item keys; keep native quantity/remove controls current.
    (cart.items || []).forEach((item, index) => {
      document.querySelectorAll(`[data-index="${index + 1}"], [data-quantity-variant-id="${item.variant_id}"]`).forEach(node => {
        if (node.hasAttribute('data-key')) node.setAttribute('data-key', item.key);
      });
    });
    const itemsSubtotal = cart.items_subtotal_price ?? (cart.items || []).reduce((sum, item) => sum + item.final_line_price, 0);
    const orderDiscount = (cart.cart_level_discount_applications || []).reduce((sum, entry) => sum + (entry.amount ?? (entry.discount_application || entry).total_allocated_amount ?? 0), 0);
    const subtotal = Math.max(0, itemsSubtotal - orderDiscount);
    document.querySelectorAll('[data-brandos-cart-total], cart-drawer .cart_total span:last-child').forEach(node => {
      node.textContent = money(subtotal, cart.currency);
    });
    document.querySelectorAll('[data-brandos-cart-discounts]').forEach(list => {
      list.replaceChildren();
      (cart.cart_level_discount_applications || []).forEach(entry => {
        const discount = entry.discount_application || entry;
        const li = document.createElement('li');
        li.textContent = `${discount.title}: -${money(entry.amount ?? discount.total_allocated_amount, cart.currency)}`;
        list.append(li);
      });
    });
    const drawerList = document.querySelector('cart-drawer .cart_btns .discounts');
    if (drawerList) {
      drawerList.replaceChildren();
      (cart.cart_level_discount_applications || []).forEach(entry => {
        const discount = entry.discount_application || entry;
        const li = document.createElement('li');
        li.className = 'discounts__discount discounts__discount--end';
        li.textContent = `${discount.title} (-${money(entry.amount ?? discount.total_allocated_amount, cart.currency)})`;
        drawerList.append(li);
      });
    }
  };
  const fetchJson = async (path, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(rootPath() + path, { ...options, signal: controller.signal });
      if (!response.ok) throw new Error('cart_request_failed');
      return await response.json();
    } finally { clearTimeout(timer); }
  };
  const fetchCart = () => fetchJson('cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
  const saveMode = async mode => {
    if (!validMode(mode)) throw new Error('invalid_payment_preference');
    const before = await fetchCart();
    const codes = codesFromCart(before);
    const otherCodes = codes.filter(code => code.toUpperCase() !== 'PREPAID5');
    // Keep an existing campaign/customer coupon. Never replace it with PREPAID5.
    const nextCodes = mode === 'prepaid' && !otherCodes.length ? ['PREPAID5'] : otherCodes;
    const cart = await fetchJson('cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: {
          brandos_payment_preference: mode === 'prepaid' ? 'PREPAID' : 'COD',
          brandos_prepaid_offer: mode === 'prepaid' ? 'PREPAID5' : ''
        },
        discount: nextCodes.join(',')
      })
    });
    if (modeFromCart(cart) !== mode) throw new Error('preference_not_saved');
    return cart;
  };
  const runSync = async () => {
    let cart;
    do {
      const target = desiredMode;
      cart = await saveMode(target);
      if (target !== desiredMode) continue;
      paint(target);
      renderTotals(cart);
      const prepaidApplied = (cart.discount_codes || []).some(code => code.code.toUpperCase() === 'PREPAID5' && code.applicable);
      const message = target === 'cod'
        ? 'COD preference saved. INR 50 COD fee applies where COD is available; confirm the final total at checkout.'
        : prepaidApplied
          ? 'Online payment selected. PREPAID5 discount applied. Choose UPI or card at secure checkout.'
          : 'Online payment selected. Your existing coupon is kept; prepaid savings cannot be stacked with it.';
      prompt(message);
      return cart;
    } while (true);
  };
  const sync = mode => {
    desiredMode = mode;
    if (!runner) runner = runSync().finally(() => { runner = null; });
    return runner;
  };
  const currentChoice = () => {
    const input = document.querySelector('[data-brandos-cart-choice]:checked');
    return validMode(input?.value) ? input.value : '';
  };

  document.addEventListener('change', event => {
    const input = event.target;
    if (!(input instanceof Element) || !input.matches('[data-brandos-cart-choice]')) return;
    if (checkoutBusy) return;
    paint(input.value);
    prompt('Saving your payment preference...');
    sync(input.value).catch(() => prompt('Unable to save your preference. Check your connection and choose again before checkout.', true));
  });

  document.addEventListener('submit', async event => {
    const form = event.target;
    const button = event.submitter;
    if (!(form instanceof HTMLFormElement) || button?.name !== 'checkout') return;
    if (allowedSubmits.has(form)) { allowedSubmits.delete(form); return; }
    if (!boxes().length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutBusy) return;
    const mode = currentChoice();
    if (!mode) {
      prompt('Choose online payment or Cash on Delivery here to continue to checkout.', true);
      boxes().find(box => box.getBoundingClientRect().height > 0)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    checkoutBusy = true;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    boxes().forEach(box => box.querySelectorAll('input').forEach(input => { input.disabled = true; }));
    try {
      prompt('Confirming your payment preference...');
      await sync(mode);
      // Wait for cart attributes and discount to persist before the native checkout POST.
      const verified = await fetchCart();
      if (modeFromCart(verified) !== mode) throw new Error('preference_changed');
      if (mode === 'cod' && (verified.discount_codes || []).some(code => code.code.toUpperCase() === 'PREPAID5' && code.applicable)) throw new Error('prepaid_discount_on_cod');
      allowedSubmits.add(form);
      button.disabled = false;
      form.requestSubmit(button);
    } catch (error) {
      prompt('Unable to confirm your payment preference. Your cart is saved. Please try again.', true);
    } finally {
      checkoutBusy = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      boxes().forEach(box => box.querySelectorAll('input').forEach(input => { input.disabled = false; }));
    }
  }, true);
  // Deliberately no cart write, discount change, localStorage reset, or add-to-cart interception on page load.
})();
